import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formations } from "@/mocks/formations";
import SectionHeader from "@/components/base/SectionHeader";

export default function FormationsSection() {
  const { t } = useTranslation();

  const levelKeyMap: Record<string, string> = {
    "Débutant": "formations.level.beginner",
    "Intermédiaire": "formations.level.intermediate",
    "Avancé": "formations.level.advanced",
  };

  const levelColors: Record<string, string> = {
    "Débutant": "bg-green-50 text-green-700",
    "Intermédiaire": "bg-amber-50 text-amber-700",
    "Avancé": "bg-red-50 text-red-600",
  };

  const featured = formations.filter((f) => f.featured);
  const others = formations.filter((f) => !f.featured).slice(0, 3);

  return (
    <section className="py-16 bg-[#fdf8f3]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <SectionHeader
          title={t("section.formations.title")}
          subtitle={t("section.formations.subtitle")}
          viewAllLink="/formations"
          accent="orange"
        />

        {/* Wazi Partner Banner */}
        <div className="mb-8 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
              <i className="ri-award-line text-white text-2xl"></i>
            </div>
            <div>
              <p className="text-white font-bold text-sm">{t("formations.partner.title")}</p>
              <p className="text-white/80 text-xs">{t("formations.partner.desc")}</p>
            </div>
          </div>
          <Link
            to="/formations"
            className="flex-shrink-0 bg-white text-orange-600 font-bold text-sm px-5 py-2.5 rounded-full hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("formations.partner.cta")}
          </Link>
        </div>

        {/* Featured formations */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-6">
          {featured.map((f) => (
            <a
              key={f.id}
              href={f.link.startsWith("/") ? undefined : f.link}
              target={f.link.startsWith("/") ? undefined : "_blank"}
              rel={f.link.startsWith("/") ? undefined : "nofollow noopener noreferrer"}
              className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-orange-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer block"
            >
              <div className="relative h-44 overflow-hidden">
                <img src={f.image} alt={f.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute top-3 left-3 flex gap-2">
                  {f.partnerWazi && (
                    <span className="bg-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-300 text-xs"></i>Wazi Partner
                    </span>
                  )}
                  {f.certification && (
                    <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full">
                      {t("formations.certified")}
                    </span>
                  )}
                </div>
                <div className="absolute bottom-3 left-3 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg overflow-hidden bg-white">
                    <img src={f.providerLogo} alt={f.provider} className="w-full h-full object-cover" />
                  </div>
                  <span className="text-white text-xs font-semibold">{f.provider}</span>
                </div>
              </div>

              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${levelColors[f.level]}`}>
                    {t(levelKeyMap[f.level] ?? f.level)}
                  </span>
                  <span className="text-xs text-gray-400 flex items-center gap-1"><i className="ri-time-line"></i>{f.duration}</span>
                  <span className="text-xs text-gray-400 flex items-center gap-1"><i className="ri-global-line"></i>{f.format}</span>
                </div>
                <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2 mb-2 group-hover:text-orange-500 transition-colors">{f.title}</h3>
                <p className="text-xs text-gray-500 line-clamp-2 mb-3">{f.description}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {f.skills.slice(0, 3).map((s) => (
                    <span key={s} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{s}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <span className={`text-sm font-bold ${f.price === "Gratuit" || f.price === "Free" ? "text-green-600" : "text-gray-900"}`}>
                    {f.price}
                  </span>
                  <span className="text-orange-500 text-xs font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                    {t("formations.register")} <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Other formations */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {others.map((f) => (
            <a
              key={f.id}
              href={f.link}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="group flex items-center gap-4 bg-white rounded-2xl border border-gray-100 hover:border-orange-200 p-4 transition-all cursor-pointer"
            >
              <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                <img src={f.providerLogo} alt={f.provider} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 line-clamp-1 group-hover:text-orange-500 transition-colors">{f.title}</h4>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${levelColors[f.level]}`}>
                    {t(levelKeyMap[f.level] ?? f.level)}
                  </span>
                  <span className={`text-xs font-bold ${f.price === "Gratuit" || f.price === "Free" ? "text-green-600" : "text-gray-600"}`}>{f.price}</span>
                </div>
              </div>
              <i className="ri-arrow-right-up-line text-gray-300 group-hover:text-orange-400 transition-colors flex-shrink-0"></i>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
